import React from "react";

import ColorThemePicker from "../ColorThemePicker";
import EditableZone from "../EditableZone";

import type { EmailTheme } from "../data/colorThemes.data";
import type { TemplateZones } from "../data/sampleEmailTemplates.data";

interface Props {
  zones: TemplateZones;
  theme: EmailTheme;
  headerTitle: string;
  headerSubtitle: string;
  headerEyebrow?: string;
  onZoneChange: (zone: keyof TemplateZones, html: string) => void;
  onThemeChange: (theme: EmailTheme) => void;
  onHeaderTitleChange: (v: string) => void;
  onHeaderSubtitleChange: (v: string) => void;
  onHeaderEyebrowChange?: (v: string) => void;
  brandName?: string;
  companyName?: string;
  logoUrl?: string;
}

const DEFAULT_BRAND = "Zyvo";
const DEFAULT_COMPANY = "Lakhera Global Services";
const DEFAULT_LOGO = "https://app.zyvolabs.com/assets/images/logo.svg";
const LOGO_DISPLAY_HEIGHT = 44;

const TemplateCanvas: React.FC<Props> = ({
  zones,
  theme,
  headerTitle,
  headerSubtitle,
  headerEyebrow = "",
  onZoneChange,
  onThemeChange,
  onHeaderTitleChange,
  onHeaderSubtitleChange,
  onHeaderEyebrowChange,
  brandName = DEFAULT_BRAND,
  companyName = DEFAULT_COMPANY,
  logoUrl = DEFAULT_LOGO,
}) => (
  <div
    className="email-canvas-wrapper"
    style={{ background: "#eef2f7", padding: "16px 0" }}
  >
    <div
      className="email-canvas"
      style={{
        background: theme.bodyBg,
        color: theme.bodyText,
        border: "1px solid #e2e8f0",
        borderRadius: 8,
        overflow: "hidden",
        maxWidth: 640,
        margin: "0 auto",
        position: "relative",
        ["--et-link-color" as any]: theme.linkColor,
      }}
    >
      <style>{`
        .email-body-container a:not(.et-cta-btn) { color: ${theme.linkColor}; }
        .email-body-container .zone-content a { color: ${theme.linkColor}; }
      `}</style>
      <div
        style={{ position: "absolute", top: 10, right: 10, zIndex: 2 }}
      >
        <ColorThemePicker value={theme} onChange={onThemeChange} />
      </div>

      <div
        style={{
          height: 6,
          background: theme.primary,
          fontSize: 0,
          lineHeight: 0,
        }}
      />

      <div
        style={{
          padding: "28px 28px 24px",
          borderBottom: "1px solid #e2e8f0",
          textAlign: "center",
          background: theme.bodyBg,
        }}
      >
        <img
          src={logoUrl}
          alt={brandName}
          height={LOGO_DISPLAY_HEIGHT}
          style={{
            display: "inline-block",
            height: LOGO_DISPLAY_HEIGHT,
            width: "auto",
            maxWidth: "70%",
            verticalAlign: "middle",
            border: 0,
            outline: "none",
            font: "800 22px/44px 'Inter',Arial,sans-serif",
            color: "#0f172a",
            letterSpacing: "0.04em",
            textTransform: "uppercase",
          }}
        />
      </div>

      <div
        style={{
          padding: "40px 28px 36px",
          background: theme.canvasBg,
          textAlign: "center",
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 14,
            marginBottom: 14,
          }}
        >
          <span
            style={{
              display: "inline-block",
              width: 32,
              height: 0,
              borderTop: `1px solid ${theme.primary}`,
              opacity: 0.4,
            }}
          />
          <input
            value={headerEyebrow}
            onChange={(e) => onHeaderEyebrowChange?.(e.target.value)}
            placeholder="EYEBROW (e.g. SIGN-IN VERIFICATION)"
            style={{
              background: "transparent",
              border: "none",
              outline: "none",
              color: theme.primary,
              font: "700 11px/16px 'Inter',Arial,sans-serif",
              textTransform: "uppercase",
              letterSpacing: "0.16em",
              textAlign: "center",
              width: Math.max(220, (headerEyebrow.length || 18) * 9),
              borderBottom: "1px dashed transparent",
              padding: "2px 4px",
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderBottom = `1px dashed ${theme.primary}`;
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderBottom = "1px dashed transparent";
            }}
          />
          <span
            style={{
              display: "inline-block",
              width: 32,
              height: 0,
              borderTop: `1px solid ${theme.primary}`,
              opacity: 0.4,
            }}
          />
        </div>

        <input
          value={headerTitle}
          onChange={(e) => onHeaderTitleChange(e.target.value)}
          placeholder="Email title (e.g. Verify your sign-in)"
          style={{
            background: "transparent",
            border: "none",
            outline: "none",
            color: "#0f172a",
            font: "800 26px/34px 'Inter',Arial,sans-serif",
            letterSpacing: "-0.02em",
            textAlign: "center",
            width: "100%",
            padding: "2px 4px",
            marginBottom: 10,
            borderBottom: "1px dashed transparent",
          }}
          onFocus={(e) => {
            e.currentTarget.style.borderBottom = "1px dashed #cbd5e1";
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderBottom = "1px dashed transparent";
          }}
        />

        <input
          value={headerSubtitle}
          onChange={(e) => onHeaderSubtitleChange(e.target.value)}
          placeholder="Subtitle (optional)"
          style={{
            background: "transparent",
            border: "none",
            outline: "none",
            color: "#475569",
            font: "400 14px/22px 'Inter',Arial,sans-serif",
            textAlign: "center",
            width: "100%",
            maxWidth: 460,
            margin: "0 auto",
            padding: "2px 4px",
            borderBottom: "1px dashed transparent",
          }}
          onFocus={(e) => {
            e.currentTarget.style.borderBottom = "1px dashed #cbd5e1";
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderBottom = "1px dashed transparent";
          }}
        />
      </div>

      <div
        className="email-body-container"
        style={{ background: theme.bodyBg, color: theme.bodyText, padding: "0 12px" }}
      >
        <EditableZone
          label="Greeting"
          content={zones.greeting}
          onChange={(html) => onZoneChange("greeting", html)}
          placeholder="e.g. Hi {{name}},"
          themePrimary={theme.primary}
        />
        <EditableZone
          label="Body"
          content={zones.body}
          onChange={(html) => onZoneChange("body", html)}
          placeholder="Write the main content of your email here…"
          themePrimary={theme.primary}
          allowRichInserts
        />
        <EditableZone
          label="Closing"
          content={zones.closing}
          onChange={(html) => onZoneChange("closing", html)}
          placeholder="e.g. Best regards, The Team"
          themePrimary={theme.primary}
        />
      </div>

      <div
        style={{
          padding: "24px 28px",
          background: "#f8fafc",
          textAlign: "center",
          borderTop: "1px solid #e2e8f0",
        }}
      >
        <div
          style={{
            font: "400 12px/18px 'Inter',Arial,sans-serif",
            color: "#64748b",
          }}
        >
          This is an automated email · please do not reply directly to this
          message.
          <br />ZYVO · A product by {companyName}
          <br />© {new Date().getFullYear()} {companyName}. All rights reserved.
        </div>
      </div>
    </div>
  </div>
);

export default TemplateCanvas;
